import React, { useCallback, useEffect, useState } from 'react';
import {
  FaCheck,
  FaClipboardList,
  FaExclamationTriangle,
  FaSearch,
  FaShieldAlt,
  FaSpinner,
  FaTimes,
  FaUserShield,
  FaUsers,
} from 'react-icons/fa';
import Seo from '../components/Seo';
import {
  approveVerificationReview,
  rejectVerificationReview,
} from '../services/verificationReview';
import {
  canAdminReview,
  getVerificationStatusMeta,
} from '../utils/verificationStatus';
import AdminUsersPanel from './AdminUsersPanel';
import './AdminPage.css';

const ADMIN_TABS = [
  { id: 'tracking', label: 'Tracking', icon: FaClipboardList },
  { id: 'users', label: 'Users', icon: FaUsers },
];

const PAGE_SIZE = 10;

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'paid', label: 'Paid' },
  { id: 'unpaid', label: 'Unpaid' },
  { id: 'awaiting-review', label: 'Awaiting review' },
  { id: 'pending', label: 'Pending notification' },
  { id: 'notification-sent', label: 'Notification sent' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'refunded', label: 'Refunded' },
  { id: 'failed', label: 'Failed' },
];

function formatAmount(item) {
  const amount = item.amountPaidInr ?? item.amountExpectedInr;
  if (amount == null) return '—';
  return `₹${amount.toLocaleString('en-IN')}`;
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatEnum(value) {
  if (!value) return '—';
  return String(value).replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

function getOverallStatusMeta(item) {
  const meta = getVerificationStatusMeta(item);
  return {
    ...meta,
    className: meta.className.replace(/^status-/, 'admin-status-'),
  };
}

function matchesClientFilter(item, filter) {
  const meta = getOverallStatusMeta(item);
  if (filter === 'awaiting-review') return meta.className === 'admin-status-review-pending';
  if (filter === 'rejected') return meta.className === 'admin-status-rejected';
  if (filter === 'pending') return meta.className === 'admin-status-pending';
  if (filter === 'notification-sent') return meta.className === 'admin-status-notified';
  if (filter === 'paid') return item.paymentStatus === 'PAID';
  if (filter === 'unpaid') return item.paymentStatus === 'UNPAID';
  if (filter === 'refunded') return item.paymentStatus === 'REFUNDED';
  if (filter === 'failed') return item.verificationStatus === 'FAILED';
  return true;
}

function getTotalPages(total, limit) {
  if (!total || total <= 0) return 1;
  return Math.max(1, Math.ceil(total / limit));
}

function getVisiblePages(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
  return [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
}

function formatCheckType(checkType) {
  if (checkType === 'NOMINEE_LOOKUP') return 'Nominee lookup';
  return 'Notify all nominees';
}

function getSubjectLabel(item) {
  if (item.checkType === 'NOMINEE_LOOKUP') {
    return item.targetNomineeEmail || item.targetNomineePhone || '—';
  }
  return item.policyholderEmail || item.policyholderPhone || '—';
}

function AdminReviewActions({ item, busy, onApprove, onReject }) {
  if (!canAdminReview(item)) {
    return <span className="admin-action-muted">—</span>;
  }

  return (
    <div className="admin-review-actions">
      <button
        type="button"
        className="admin-review-btn admin-review-btn-approve"
        onClick={() => onApprove(item)}
        disabled={busy}
      >
        {busy ? <FaSpinner className="admin-spin-icon" aria-hidden="true" /> : <FaCheck aria-hidden="true" />}
        Approve
      </button>
      <button
        type="button"
        className="admin-review-btn admin-review-btn-reject"
        onClick={() => onReject(item)}
        disabled={busy}
      >
        <FaTimes aria-hidden="true" />
        Reject
      </button>
    </div>
  );
}

function AdminPage({ user }) {
  const [tab, setTab] = useState('tracking');
  const [userTotal, setUserTotal] = useState(null);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState(null);
  const [actionError, setActionError] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const isSearchPending = searchInput.trim() !== search;
  const isUsersTab = tab === 'users';

  const fetchAdminData = useCallback(async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');

    try {
      const reviewFilters = ['awaiting-review', 'rejected'];
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });

      if (reviewFilters.includes(filter)) {
        params.set('reviewStatus', filter === 'awaiting-review' ? 'PENDING_REVIEW' : 'REJECTED');
      } else {
        params.set('paymentStatus', filter);
      }

      if (search) {
        params.set('search', search);
      }

      const response = await fetch(
        `${process.env.REACT_APP_HOST_SERVER}/api/admin/verifications?${params.toString()}`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (response.status === 403) {
        throw new Error('You do not have admin access.');
      }
      if (!response.ok) {
        throw new Error('Unable to load admin tracking data.');
      }

      const json = await response.json();
      if (!json.successful) {
        throw new Error(json.message || 'Unable to load admin tracking data.');
      }

      let nextItems = json.data?.items || [];
      let nextPagination = json.data?.pagination || { page, limit: PAGE_SIZE, total: 0 };

      if (reviewFilters.includes(filter) && nextItems.length === 0 && !search) {
        const fallbackParams = new URLSearchParams({
          paymentStatus: 'all',
          page: String(page),
          limit: String(PAGE_SIZE),
        });
        const fallback = await fetch(
          `${process.env.REACT_APP_HOST_SERVER}/api/admin/verifications?${fallbackParams.toString()}`,
          { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
        );
        const fallbackJson = await fallback.json();
        if (fallback.ok && fallbackJson.successful) {
          const filtered = (fallbackJson.data?.items || []).filter((item) => matchesClientFilter(item, filter));
          nextItems = filtered;
          nextPagination = {
            ...(fallbackJson.data?.pagination || nextPagination),
            total: filtered.length,
          };
        }
      }

      setItems(nextItems);
      setPagination(nextPagination);
    } catch (err) {
      setItems([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0 });
      setError(err.message || 'Unable to load admin tracking data.');
    } finally {
      setLoading(false);
    }
  }, [filter, page, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [filter, search]);

  useEffect(() => {
    if (isUsersTab) {
      return;
    }
    if (user) {
      fetchAdminData();
    } else {
      setLoading(false);
      setItems([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0 });
    }
  }, [user, fetchAdminData, isUsersTab]);

  const totalPages = getTotalPages(pagination.total, pagination.limit || PAGE_SIZE);
  const visiblePages = getVisiblePages(page, totalPages);
  const rangeStart = pagination.total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = pagination.total === 0 ? 0 : Math.min(page * PAGE_SIZE, pagination.total);

  const handleFilterChange = (nextFilter) => {
    setFilter(nextFilter);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  const handleApprove = async (item) => {
    const token = localStorage.getItem('token');
    setActionId(item.id);
    setActionError('');
    try {
      await approveVerificationReview(item.id, token);
      await fetchAdminData();
    } catch (err) {
      setActionError(err.message || 'Unable to approve this verification.');
    } finally {
      setActionId(null);
    }
  };

  const handleRejectOpen = (item) => {
    setRejectTarget(item);
    setRejectReason('');
    setActionError('');
  };

  const handleRejectClose = () => {
    if (actionId) return;
    setRejectTarget(null);
    setRejectReason('');
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    const token = localStorage.getItem('token');
    setActionId(rejectTarget.id);
    setActionError('');
    try {
      await rejectVerificationReview(rejectTarget.id, token, rejectReason);
      setRejectTarget(null);
      setRejectReason('');
      await fetchAdminData();
    } catch (err) {
      setActionError(err.message || 'Unable to reject this verification.');
    } finally {
      setActionId(null);
    }
  };

  return (
    <>
      <Seo
        title={isUsersTab ? 'Admin Users' : 'Admin Tracking'}
        description="Admin dashboard for users, verification, payment, and notification tracking on KeepMyAsset."
        path="/admin"
        noindex
      />
      <div className="admin-page">
        <header className="admin-header">
          <div className="admin-header-top">
            <div>
              <p className="admin-eyebrow">Admin</p>
              <h1>
                <FaUserShield aria-hidden="true" />
                {isUsersTab ? 'All Users' : 'Verification & Payment Tracking'}
              </h1>
              <p className="admin-subtitle">
                {isUsersTab
                  ? 'See every registered user. Recently added accounts appear at the top.'
                  : 'Review secure-mode requests, approve or reject manual verifications, and track notification delivery across users.'}
              </p>
            </div>
          </div>

          <div className="admin-tabs" role="tablist" aria-label="Admin sections">
            {ADMIN_TABS.map(({ id, label, icon: TabIcon }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                className={`admin-tab ${tab === id ? 'active' : ''}`}
                onClick={() => setTab(id)}
              >
                <TabIcon aria-hidden="true" />
                {label}
                {id === 'users' && userTotal != null ? (
                  <span className="admin-tab-count">{userTotal.toLocaleString('en-IN')}</span>
                ) : null}
              </button>
            ))}
          </div>

          {isUsersTab ? null : (
          <div className="admin-search-bar">
            <label className="admin-search-field" htmlFor="admin-name-search">
              <FaSearch className="admin-search-icon" aria-hidden="true" />
              <input
                id="admin-name-search"
                type="text"
                className="admin-search-input"
                placeholder="Search by initiator, policyholder, or nominee email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </label>
            {searchInput ? (
              <button type="button" className="admin-search-clear" onClick={handleClearSearch}>
                Clear
              </button>
            ) : null}
          </div>
          )}
        </header>

        {isUsersTab ? (
          <AdminUsersPanel user={user} onTotalChange={setUserTotal} />
        ) : (
        <>
        {actionError ? (
          <div className="admin-action-banner admin-action-banner-error" role="alert">
            <FaExclamationTriangle aria-hidden="true" />
            <p>{actionError}</p>
            <button type="button" onClick={() => setActionError('')}>Dismiss</button>
          </div>
        ) : null}

        <div className="admin-filters">
          <label className="admin-filter-select-wrap" htmlFor="admin-filter-select">
            <span className="admin-filter-select-label">Status</span>
            <select
              id="admin-filter-select"
              className="admin-filter-select"
              value={filter}
              onChange={(e) => handleFilterChange(e.target.value)}
              aria-label="Filter admin tracking"
            >
              {FILTERS.map(({ id, label }) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <div className="admin-filter-pills" role="tablist" aria-label="Filter admin tracking">
            {FILTERS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={filter === id}
                className={`admin-filter ${filter === id ? 'active' : ''}`}
                onClick={() => handleFilterChange(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading || isSearchPending ? (
          <div className="admin-state">
            <FaSpinner className="admin-spin-icon" aria-hidden="true" />
            <p>Loading tracking data…</p>
          </div>
        ) : error ? (
          <div className="admin-state admin-state-error">
            <FaExclamationTriangle aria-hidden="true" />
            <p>{error}</p>
            <button type="button" onClick={fetchAdminData}>Try again</button>
          </div>
        ) : items.length === 0 ? (
          <div className="admin-state">
            <FaClipboardList aria-hidden="true" />
            <p>{search ? `No records found for "${search}".` : 'No verification records found for this filter.'}</p>
          </div>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Initiated by</th>
                    <th>Check type</th>
                    <th>Subject</th>
                    <th>Amount</th>
                    <th>Payment</th>
                    <th>Overall</th>
                    <th>Email</th>
                    <th>SMS</th>
                    <th>Verification mode</th>
                    <th>Checked at</th>
                    <th>Paid at</th>
                    <th>Review</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const status = getOverallStatusMeta(item);
                    const StatusIcon = status.icon;
                    const busy = actionId === item.id;
                    return (
                      <tr key={item.id}>
                        <td data-label="Initiated by">
                          <strong>{item.initiatedByName || '—'}</strong>
                          <span className="admin-subtext">{item.initiatedByEmail || `User #${item.initiatedByUserId}`}</span>
                        </td>
                        <td data-label="Check type">{formatCheckType(item.checkType)}</td>
                        <td data-label="Subject">
                          {getSubjectLabel(item)}
                          {item.checkType === 'NOMINEE_LOOKUP' && item.targetNomineePhone && item.targetNomineeEmail ? (
                            <span className="admin-subtext">{item.targetNomineePhone}</span>
                          ) : null}
                        </td>
                        <td data-label="Amount">
                          <span>{formatAmount(item)}</span>
                          {item.amountPaidInr != null && item.amountExpectedInr != null && item.amountPaidInr !== item.amountExpectedInr ? (
                            <span className="admin-subtext">Expected ₹{item.amountExpectedInr.toLocaleString('en-IN')}</span>
                          ) : null}
                        </td>
                        <td data-label="Payment">{formatEnum(item.paymentStatus)}</td>
                        <td data-label="Overall">
                          <span className={`admin-status-badge ${status.className}`}>
                            <StatusIcon aria-hidden="true" />
                            {status.label}
                          </span>
                        </td>
                        <td data-label="Email">{formatEnum(item.emailNotificationStatus)}</td>
                        <td data-label="SMS">{formatEnum(item.smsNotificationStatus)}</td>
                        <td data-label="Verification mode">
                          <span className={`admin-mode-badge ${item.secureMode ? 'secure' : 'automatic'}`}>
                            {item.secureMode ? <FaShieldAlt aria-hidden="true" /> : null}
                            {item.verificationMode || (item.secureMode ? 'Manual verification' : 'Automatic verification')}
                          </span>
                        </td>
                        <td data-label="Checked at">{formatDateTime(item.checkedAt)}</td>
                        <td data-label="Paid at">{formatDateTime(item.paidAt)}</td>
                        <td data-label="Review">
                          <AdminReviewActions
                            item={item}
                            busy={busy}
                            onApprove={handleApprove}
                            onReject={handleRejectOpen}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="admin-cards">
              {items.map((item) => {
                const status = getOverallStatusMeta(item);
                const StatusIcon = status.icon;
                const busy = actionId === item.id;
                return (
                  <article key={item.id} className="admin-card">
                    <div className="admin-card-top">
                      <div>
                        <strong>{item.initiatedByName || `User #${item.initiatedByUserId}`}</strong>
                        <p className="admin-subtext">{item.initiatedByEmail || '—'}</p>
                      </div>
                      <span className={`admin-status-badge ${status.className}`}>
                        <StatusIcon aria-hidden="true" />
                        {status.label}
                      </span>
                    </div>
                    <dl>
                      <div>
                        <dt>Check type</dt>
                        <dd>{formatCheckType(item.checkType)}</dd>
                      </div>
                      <div>
                        <dt>Subject</dt>
                        <dd>{getSubjectLabel(item)}</dd>
                      </div>
                      <div>
                        <dt>Amount</dt>
                        <dd>{formatAmount(item)}</dd>
                      </div>
                      <div>
                        <dt>Payment</dt>
                        <dd>{formatEnum(item.paymentStatus)}</dd>
                      </div>
                      <div>
                        <dt>Email / SMS</dt>
                        <dd>{formatEnum(item.emailNotificationStatus)} / {formatEnum(item.smsNotificationStatus)}</dd>
                      </div>
                      <div>
                        <dt>Verification mode</dt>
                        <dd>{item.verificationMode || '—'}</dd>
                      </div>
                      <div>
                        <dt>Checked / Paid</dt>
                        <dd>{formatDateTime(item.checkedAt)} / {formatDateTime(item.paidAt)}</dd>
                      </div>
                    </dl>
                    {canAdminReview(item) ? (
                      <div className="admin-card-review">
                        <p>Secure-mode request awaiting manual review.</p>
                        <AdminReviewActions
                          item={item}
                          busy={busy}
                          onApprove={handleApprove}
                          onReject={handleRejectOpen}
                        />
                      </div>
                    ) : null}
                    {item.razorpayOrderId ? (
                      <p className="admin-meta-line"><strong>Order:</strong> {item.razorpayOrderId}</p>
                    ) : null}
                    {item.failureReason ? (
                      <p className="admin-failure">{item.failureReason}</p>
                    ) : null}
                  </article>
                );
              })}
            </div>

            <div className="admin-pagination" aria-label="Admin tracking pagination">
              <p className="admin-pagination-summary">
                Showing {rangeStart}–{rangeEnd} of {pagination.total} records
              </p>
              <div className="admin-pagination-controls">
                <button
                  type="button"
                  className="admin-pagination-btn"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1 || loading}
                >
                  Previous
                </button>
                <div className="admin-pagination-pages">
                  {visiblePages.map((pageNumber, index) => {
                    const prevPage = visiblePages[index - 1];
                    const showEllipsis = prevPage && pageNumber - prevPage > 1;
                    return (
                      <React.Fragment key={pageNumber}>
                        {showEllipsis ? <span className="admin-pagination-ellipsis">…</span> : null}
                        <button
                          type="button"
                          className={`admin-pagination-page ${pageNumber === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNumber)}
                          disabled={loading}
                          aria-current={pageNumber === page ? 'page' : undefined}
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="admin-pagination-btn"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages || loading || pagination.total === 0}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
        </>
        )}
      </div>

      {rejectTarget ? (
        <div className="admin-modal-backdrop" role="presentation" onClick={handleRejectClose}>
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-reject-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="admin-reject-title">Reject verification request</h2>
            <p>
              Rejecting will notify <strong>{getSubjectLabel(rejectTarget)}</strong> by email that their request was not approved.
            </p>
            <label className="admin-modal-field" htmlFor="admin-reject-reason">
              Reason (optional)
              <textarea
                id="admin-reject-reason"
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Brief reason shown in the rejection email, if supported by the backend."
              />
            </label>
            <div className="admin-modal-actions">
              <button type="button" className="admin-modal-btn admin-modal-btn-secondary" onClick={handleRejectClose} disabled={Boolean(actionId)}>
                Cancel
              </button>
              <button type="button" className="admin-modal-btn admin-modal-btn-danger" onClick={handleRejectConfirm} disabled={Boolean(actionId)}>
                {actionId ? <FaSpinner className="admin-spin-icon" aria-hidden="true" /> : null}
                Confirm reject
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default AdminPage;
